import { useEffect, useRef } from 'react'
import cc from 'classcat'

import type { LinkTooltipSettingsLayerProps, TooltipContentProps } from './LinkTooltip.utils'
import {
  SET_CATEGORY_STATE,
  SET_ERROR_STATE,
  SET_IS_EDITING,
  SET_LINK_DETAILS,
  dropdownCategoryInitialState,
} from './LinkTooltip.constants'
import { formatHref } from '../../../utils/link-sanitizer'
import { isExternalURL, isValidHref } from '../../text/links'
import { useTooltipState } from './LinkTooltipContext'
import EditMode from './components/EditMode'
import SettingsLayerTooltipStateProvider from './LinkTooltipSettingsLayerContext'
import ViewMode from './components/ViewMode'

function TooltipContent({ link, setLink, isSettingsLayer, disabled }: TooltipContentProps) {
  const { linkTooltipState, linkTooltipDispatch, categoryDispatch, errorState, errorDispatch } = useTooltipState()
  const { isViewMode } = linkTooltipState
  const containerRef = useRef<HTMLDivElement>(null)

  function handleInsertLink() {
    if (!isValidHref(linkTooltipState.url)) {
      errorDispatch({ type: SET_ERROR_STATE, payload: { hasError: true } })
      return
    }

    const formattedUrl = formatHref(linkTooltipState.url)
    const isExternal = isExternalURL(formattedUrl)

    setLink(formattedUrl)
    linkTooltipDispatch({
      type: SET_LINK_DETAILS,
      payload: {
        isViewMode: true,
        isExternalLink: isExternal,
      },
    })
  }

  function handleClickOutside() {
    linkTooltipDispatch({ type: SET_IS_EDITING, payload: false })

    if (errorState.hasError) {
      errorDispatch({ type: SET_ERROR_STATE, payload: { hasError: false } })
    }

    // If there's no existing link, reset to initial state
    if (!link) {
      linkTooltipDispatch({
        type: SET_LINK_DETAILS,
        payload: {
          url: '',
          inputValue: '',
          searchTerm: '',
          isViewMode: false,
        },
      })
      categoryDispatch({ type: SET_CATEGORY_STATE, payload: dropdownCategoryInitialState })
    }

    // If there's an existing link, revert to the previous link
    if (link) {
      linkTooltipDispatch({
        type: SET_LINK_DETAILS,
        payload: {
          url: link,
          inputValue: link,
          isViewMode: true,
        },
      })
    }
  }

  useEffect(() => {
    if (link) {
      linkTooltipDispatch({
        type: SET_LINK_DETAILS,
        payload: { url: link, isViewMode: true },
      })
    }
  }, [link, linkTooltipDispatch])

  return (
    <div className="settingslayer-link-tooltip" ref={containerRef}>
      {isViewMode ? (
        <ViewMode isSettingsLayer={isSettingsLayer} setLink={setLink} />
      ) : (
        <EditMode
          handleInsert={handleInsertLink}
          isSettingsLayer={isSettingsLayer}
          handleClickOutside={handleClickOutside}
          disabled={disabled}
        />
      )}
    </div>
  )
}

export default function LinkTooltipSettingsLayer({ t, link, setLink, label, disabled }: LinkTooltipSettingsLayerProps) {
  return (
    <SettingsLayerTooltipStateProvider>
      <div className={cc(['ep-form-row ep-form-row-min-margin', { disabled }])}>
        <label className="ep-form-row-label">
          <span className="ep-form-row-label-text">{t(label)}</span>
          <TooltipContent link={link} setLink={setLink} isSettingsLayer={true} disabled={disabled} />
        </label>
      </div>
    </SettingsLayerTooltipStateProvider>
  )
}
